import useMediaQuery from "@mui/material/useMediaQuery";
import { useIsClient } from "@uidotdev/usehooks";
import Cookies from "js-cookie";
import moment from "moment";
import { useEffect, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { InView } from "react-intersection-observer";
import { useDispatch, useSelector } from "react-redux";
import { toast } from "react-toastify";
import {
  formatSalary,
  getLastActiveAtTime,
} from "../../../helpers/conditional_rendering";
import {
  getCompanyDetails,
  getCompanyLogoImage,
} from "../../../helpers/data_management";
import { sendTrackingEvent } from "../../../helpers/tracking_management";
import {
  bookmarkJob,
  unBookmarkJob,
  updateSelectedJob,
} from "../../../redux/actions/job_action";
import {
  updateShowSignInModalStatus,
  updateSignUpOrigin,
} from "../../../redux/actions/navbar_action";
import { checkAuthentication } from "../../../redux/actions/user_action";
import * as types from "../../../redux/types/job_type";
import { runWithConnorsApplyConfirm } from "../../shared/ConnorsApplyConfirm/runWithConnorsApplyConfirm";
import {
  ArrowIconStyled,
  BookmarkButton,
  BottomChipsContainer,
  BottomContainer,
  CardVisibleLayer,
  CardWrapper,
  CompanyActivityIcon,
  CompanyActivityText,
  CompanyDetailsContainer,
  CompanyLogo,
  CompanyNameRow,
  CompanyTextContainer,
  CompanyVerifiedBadge,
  DividerStyled,
  DollarIcon,
  GlobalIcon,
  HiddenApplyButton,
  HighlightContainer,
  HighlightedContainer,
  HighlightIcon,
  JobCardSubContainer,
  JobCompanyText,
  JobDetailText,
  JobSubDetailsContainer,
  JobTitleContainer,
  JobTitleRow,
  JobTitleText,
  JobTypeChip,
  KeywordChip,
  LinkWrapper,
  LocationIcon,
  MobileActivityRow,
  MobileApplyButton,
  MobileApplyContainer,
  MobileChipContainer,
  MobileDetailChip,
  MobileStatusBadgeRow,
  PostedTimeText,
  SaveIcon,
  SpotlightIcon,
  StatusBadgeContainer,
  StatusClockIcon,
  TooltipWrapper,
  TopContainer,
  TopRightActionContainer,
  UnsaveIcon,
  VisibilityChip,
  VisibilityContainer,
} from "./styles";

function AccordionJobCard(props) {
  const { job, onHoverEnter, onJobCardClick, onApplyJobClick } = props;

  const dispatch = useDispatch();
  const { t, i18n } = useTranslation();

  const getJobTypeLabel = (jobTypeValue) => {
    if (!jobTypeValue) return "";
    const key = jobTypeValue
      .toString()
      .toLowerCase()
      .replace(/&/g, "and")
      .replace(/[^a-z0-9]+/g, "_")
      .replace(/^_+|_+$/g, "");
    return t(`companydetail.job_type_labels.${key}`, {
      defaultValue: jobTypeValue,
    });
  };

  const getStateRegionLabel = (stateRegionValue) => {
    if (!stateRegionValue) return "";
    const key = stateRegionValue
      .toString()
      .toLowerCase()
      .replace(/&/g, "and")
      .replace(/[^a-z0-9]+/g, "_")
      .replace(/^_+|_+$/g, "");
    return t(`companies.state_region_labels.${key}`, {
      defaultValue: stateRegionValue,
    });
  };

  const isLoggedIn = checkAuthentication();

  const isClient = useIsClient();
  const isMounted = useRef(true);

  const isDesktop = useMediaQuery("(min-width: 1280px)");
  const isMobile = useMediaQuery("(max-width: 640px)");

  const hoverJob = useSelector((state) => state.jobs.hoverJob);

  const [bookmark, setBookmark] = useState(job?.bookmark);
  const [bookmarkLoading, setBookmarkLoading] = useState(false);
  const [applyButtonHover, setApplyButtonHover] = useState(false);
  const [tooltipText, setTooltipText] = useState(
    t("jobs.card_tooltip_view_job")
  );
  const [isHoveringCompanyLogo, setIsHoveringCompanyLogo] = useState(false);

  const jobDiscovery = "";
  const searchTerm = "";

  /*CONDITIONAL LOGIC */
  const activeHoverJob = hoverJob?.id === job?.id;

  const jobSlotMax = job?.jobSlotType?.includes("job_slot_max");

  const jobSlotUpgrade = jobSlotMax || job?.jobSlotType === "job_slot_plus";

  const jobSpotlight = job?.spotlight || jobSlotUpgrade;

  const isAggregatedJob = job?.category === "aggregated";

  const chipExist =
    (isClient || isMounted.current) &&
    !isMobile &&
    (job?.spotlight ||
      job?.boosted ||
      jobSlotUpgrade ||
      job?.globalHirePreferences?.rightToWorkOpenToSponsor);

  const boosted = job?.spotlight || job?.boosted || jobSlotUpgrade;

  const disabledApplyButton =
    job?.haveApplied || job?.haveApplied === undefined;

  const disabledBookmarkButton =
    (!isClient && (job?.bookmark || job?.bookmark === undefined)) ||
    bookmarkLoading;

  const jobTypeLabel = useMemo(
    () => getJobTypeLabel(job?.jobType),
    [job?.jobType, t]
  );
  const stateRegionLabel = useMemo(
    () => getStateRegionLabel(job?.stateRegion),
    [job?.stateRegion, t]
  );
  const isExternalJob =
    job?.category === "scraped" || job?.category === "aggregated";

  /* DYNAMIC TEXT LOGIC */
  const bookmarkIcon = bookmark ? <UnsaveIcon /> : <SaveIcon />;

  const applyText = job?.haveApplied
    ? t("jobs.apply_applied")
    : isMobile
    ? isExternalJob
      ? t("jobs.apply")
      : t("jobs.apply_now")
    : applyButtonHover
    ? t("jobs.apply_now_emphasis")
    : t("jobs.apply_prompt");

  /* FUNCTION LOGIC */
  function executeCompanyClick(companyDetails) {
    Cookies.set("click-source", "job_listing_card");

    if (companyDetails?.slug) {
      window.open(`/companies/${companyDetails?.slug}`, "_blank");
    }
  }

  function handleCompanyClick(event, companyDetails) {
    runWithConnorsApplyConfirm(
      job,
      () => executeCompanyClick(companyDetails),
      event
    );
  }

  function handleBookmarkJob(e) {
    e.stopPropagation();
    e.preventDefault();

    // Redirect to sign in if user is not logged in
    if (!isLoggedIn) {
      return dispatch(updateSelectedJob(job)).then(() => {
        dispatch(updateSignUpOrigin("save_job"));
        dispatch(updateShowSignInModalStatus(true));
      });
    }

    if (bookmarkLoading) {
      return;
    }

    setBookmarkLoading(true);

    let params = {
      jobId: job?.id,
      canCancel: false,
      origin: "job-list",
    };

    // Toggle bookmark status
    if (!bookmark) {
      dispatch(bookmarkJob(params)).then((res) => {
        if (res?.type === types.BOOKMARK_JOB_SUCCEED) {
          toast.success(t("jobs.job_saved_toast"));
          setBookmark(true);
          setBookmarkLoading(false);
        }
      });
    } else if (bookmark) {
      dispatch(unBookmarkJob(params)).then((res) => {
        if (res?.type === types.UNBOOKMARK_JOB_SUCCEED) {
          toast.success(t("jobs.job_unbookmarked_toast"));
          setBookmark(false);
          setBookmarkLoading(false);
        }
      });
    }
  }

  function trackImpressionInView(job) {
    // POSTHOG JOB IMPRESSION TRACKING
    sendTrackingEvent({
      event: "CE_job-impression",
      "job-id": job?.id,
      origin: "job_list",
      job_discovery: jobDiscovery,
      search_term: searchTerm ?? "",
    });
  }

  function getTime(job) {
    let activeAtDate = job?.activeAt || job?.active_at;
    let jobActiveAt = moment(activeAtDate, "YYYYMMDD");
    let timeNow = moment(moment(), "YYYYMMDD");

    const time =
      timeNow.diff(jobActiveAt, "months") > 3
        ? null
        : timeNow.diff(jobActiveAt, "months") > 1
        ? t("jobs.months_ago", { count: timeNow.diff(jobActiveAt, "months") })
        : moment(activeAtDate).fromNow();

    return time;
  }

  /* MEMO SECTION */
  const salary = useMemo(() => {
    return job?.salary === "Undisclosed" || job?.salary === "Undefined"
      ? t("jobs.salary_undisclosed")
      : formatSalary(job?.salary, job?.crossBorderData);
  }, [job?.salary, job?.crossBorderData, t]);

  const companyDetails = useMemo(() => {
    return getCompanyDetails(job);
  }, [job?.company, job?.crossBorderData]);
  const companyName = companyDetails?.name;
  const isCompanyVerified =
    companyDetails?.verified ||
    companyName === "Hiredly" ||
    companyName === "Hiredly X";

  const companyLogo = useMemo(() => {
    return getCompanyLogoImage(companyDetails);
  }, [companyDetails]);

  const jobPostedTime = useMemo(() => {
    return getTime(job) !== null ? getTime(job) : null;
  }, [job]);

  const companyLogoTooltipText = isAggregatedJob
    ? companyName
    : t("jobs.card_tooltip_company_profile");

  const tooltipViewJob = t("jobs.card_tooltip_view_job");
  const statusBadge = useMemo(() => {
    if (!job?.employerStatus) return null;

    switch (job.employerStatus) {
      case "Expiring Soon":
        return {
          text: "Expiring soon",
          color: "#F75443",
          icon: <StatusClockIcon />,
        };

      case "Actively Reviewing":
        return {
          text: "Actively reviewing",
          color: "#6AA84F",
          icon: null,
        };

      case "Be an Early Applicant":
        return {
          text: "Be an early applicant",
          color: "#512ACC",
          icon: null,
        };

      default:
        return null;
    }
  }, [job?.employerStatus]);

  const hasSpecialToken = chipExist;
  const renderStatusBadge = () =>
    statusBadge ? (
      <StatusBadgeContainer $color={statusBadge.color}>
        {statusBadge.icon}
        {statusBadge.text}
      </StatusBadgeContainer>
    ) : null;

  const tooltipApply = t("jobs.card_tooltip_apply");

  useEffect(() => {
    setTooltipText(tooltipViewJob);
  }, [tooltipViewJob, i18n.language]);

  function handleApplyClick(e) {
    onApplyJobClick(e, job);
  }

  function handleCardClick(e) {
    onJobCardClick(e, job);
  }

  return (
    <LinkWrapper
      href={`/jobs/${job?.slug}`}
      data-testid={`job-card-link-${job?.id}`}
    >
      <TooltipWrapper followCursor title={tooltipText}>
        <CardWrapper
          onClick={handleCardClick}
          onMouseEnter={() => {
            onHoverEnter(job);
            setTooltipText(tooltipViewJob);
          }}
          $selected={activeHoverJob}
          $highlight={job?.boosted}
          $spotlight={jobSpotlight}
        >
          {boosted && (
            <HighlightContainer
              $highlight={job?.boosted}
              $spotlight={jobSpotlight}
            />
          )}

          <CardVisibleLayer className="shrink-on-hover">
            <TopContainer $boosted={boosted} $hasSpecialToken={hasSpecialToken}>
              {chipExist && (
                <HighlightedContainer>
                  <VisibilityContainer>
                    {job?.boosted && (
                      <VisibilityChip $highlight>
                        <HighlightIcon />
                      </VisibilityChip>
                    )}
                    {jobSpotlight && (
                      <VisibilityChip $spotlight>
                        <SpotlightIcon />
                      </VisibilityChip>
                    )}
                    {job?.globalHirePreferences?.rightToWorkOpenToSponsor && (
                      <VisibilityChip $global>
                        <GlobalIcon />
                        {t("jobs.global_hire")}
                      </VisibilityChip>
                    )}
                  </VisibilityContainer>
                  <TopRightActionContainer>
                    <BookmarkButton
                      onClick={handleBookmarkJob}
                      disabled={disabledBookmarkButton}
                    >
                      {bookmarkIcon}
                    </BookmarkButton>
                  </TopRightActionContainer>
                </HighlightedContainer>
              )}

              {!chipExist && !isMobile && (
                <TopRightActionContainer>
                  <BookmarkButton
                    onClick={handleBookmarkJob}
                    disabled={disabledBookmarkButton}
                  >
                    {bookmarkIcon}
                  </BookmarkButton>
                </TopRightActionContainer>
              )}

              <JobTitleContainer $hasSpecialToken={hasSpecialToken}>
                <JobTitleRow>
                  <InView
                    style={{ width: "100%" }}
                    onChange={(inView) =>
                      inView === true && trackImpressionInView(job)
                    }
                  >
                    <JobTitleText $hasSpecialToken={hasSpecialToken}>
                      {job?.title}
                    </JobTitleText>
                  </InView>
                </JobTitleRow>

                {isMobile && (
                  <BookmarkButton
                    onClick={handleBookmarkJob}
                    disabled={disabledBookmarkButton}
                  >
                    {bookmarkIcon}
                  </BookmarkButton>
                )}
              </JobTitleContainer>

              {!isMobile && (
                <JobSubDetailsContainer $hasSpecialToken={hasSpecialToken}>
                  <JobDetailText>
                    {isClient && <DollarIcon />}
                    {salary}
                  </JobDetailText>
                  <JobDetailText>
                    {isClient && <LocationIcon />}
                    {stateRegionLabel}
                  </JobDetailText>
                </JobSubDetailsContainer>
              )}

              <JobCardSubContainer $hasSpecialToken={hasSpecialToken}>
                <CompanyDetailsContainer>
                  <CompanyLogo
                    src={companyLogo}
                    alt={companyName}
                    loading={"lazy"}
                    $placeholder={!companyDetails?.logo}
                    onClick={(e) => handleCompanyClick(e, companyDetails)}
                    onMouseEnter={() => {
                      setIsHoveringCompanyLogo(true);
                      setTooltipText(companyLogoTooltipText);
                    }}
                    onMouseLeave={() => {
                      setIsHoveringCompanyLogo(false);
                      setTooltipText(tooltipViewJob);
                    }}
                    style={{
                      cursor: isAggregatedJob ? "default" : "pointer",
                    }}
                  />
                  <CompanyTextContainer>
                    <CompanyNameRow>
                      <JobCompanyText>{companyName}</JobCompanyText>
                      {isCompanyVerified === true && (
                        <CompanyVerifiedBadge aria-label="Verified company" />
                      )}
                      {/* {isCompanyVerified === false && (
                        <CompanyUnverifiedBadge aria-label="Unverified company" />
                      )} */}
                    </CompanyNameRow>
                    {statusBadge && renderStatusBadge()}
                  </CompanyTextContainer>
                  {/* {!chipExist && !isMobile && (
                   <BookmarkButton
                      onClick={handleBookmarkJob}
                      disabled={disabledBookmarkButton}
                    >
                      {bookmarkIcon}
                    </BookmarkButton>
                  )} */}
                </CompanyDetailsContainer>

                {isMobile && (
                  <>
                    <MobileChipContainer>
                      <MobileDetailChip type={"jobType"}>
                        {jobTypeLabel}
                      </MobileDetailChip>
                      <MobileDetailChip type={"state"}>
                        {stateRegionLabel}
                      </MobileDetailChip>
                      <MobileDetailChip type={"salary"}>
                        {salary}
                      </MobileDetailChip>
                    </MobileChipContainer>
                    <MobileActivityRow>
                      {getLastActiveAtTime(job?.company?.lastActiveAt, t) && (
                        <CompanyActivityText>
                          <CompanyActivityIcon />
                          Last activity -{" "}
                          {getLastActiveAtTime(job?.company?.lastActiveAt, t)}
                        </CompanyActivityText>
                      )}
                      <PostedTimeText>{jobPostedTime}</PostedTimeText>
                    </MobileActivityRow>

                    {statusBadge && (
                      <MobileStatusBadgeRow>
                        {renderStatusBadge()}
                      </MobileStatusBadgeRow>
                    )}
                  </>
                )}
              </JobCardSubContainer>
            </TopContainer>

            {(!isMobile ||
              (job?.keywordHighlight &&
                job?.keywordHighlightText?.length > 0)) && (
              <BottomContainer $jobSlotMax={jobSlotMax}>
                <BottomChipsContainer>
                  {job?.jobType && !isMobile && (
                    <JobTypeChip $jobSlotMax={jobSlotMax}>
                      {jobTypeLabel}
                    </JobTypeChip>
                  )}
                  {job?.keywordHighlight && (
                    <>
                      {!isMobile && (
                        <DividerStyled
                          orientation="vertical"
                          $jobSlotMax={jobSlotMax}
                        />
                      )}
                      {job?.keywordHighlightText.map((keyword) => (
                        <KeywordChip key={keyword} $jobSlotMax={jobSlotMax}>
                          {keyword}
                        </KeywordChip>
                      ))}
                    </>
                  )}
                </BottomChipsContainer>
                {!isMobile && (
                  <PostedTimeText $jobSlotMax={jobSlotMax}>
                    {jobPostedTime}
                  </PostedTimeText>
                )}
              </BottomContainer>
            )}

            {isMobile && (
              <MobileApplyContainer>
                <MobileApplyButton
                  onClick={handleApplyClick}
                  disabled={disabledApplyButton}
                >
                  {applyText}
                  {isLoggedIn && isClient && isExternalJob && (
                    <ArrowIconStyled />
                  )}
                </MobileApplyButton>
              </MobileApplyContainer>
            )}
          </CardVisibleLayer>

          {isDesktop && (
            <HiddenApplyButton
              onClick={handleApplyClick}
              disabled={disabledApplyButton}
              onMouseEnter={() => {
                setApplyButtonHover(true);
                if (!isHoveringCompanyLogo) {
                  setTooltipText(tooltipApply);
                }
              }}
              onMouseLeave={() => {
                setApplyButtonHover(false);
                if (!isHoveringCompanyLogo) {
                  setTooltipText(tooltipViewJob);
                }
              }}
            >
              {applyText}
              {isLoggedIn && isClient && isExternalJob && <ArrowIconStyled />}
            </HiddenApplyButton>
          )}
        </CardWrapper>
      </TooltipWrapper>
    </LinkWrapper>
  );
}

export default AccordionJobCard;
